import { cn } from '@/lib/utils';
import { Icon } from '@chiroup/components/Icon';
import { Icons } from '@chiroup/components/icons';
import { RadioGroup } from '@headlessui/react';
import { CheckCircle as CheckCircleIcon } from 'lucide-react';
import React from 'react';
import { hcVerbiage } from '../../common/verbiage';
import { PatientInfo } from '../../contexts/PatientPortalContext';

type Props = {
  patients: PatientInfo[];
  onSelect: (patient: PatientInfo) => void;
  language?: 'en' | 'es';
};

const PatientSelect: React.FC<Props> = ({ patients, onSelect, language = 'en' }) => {
  return (
    <div className="mx-auto max-w-lg py-8">
      <div className="text-center mb-8">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--primary-color)] text-white">
          <Icon icon={Icons.users} className="h-7 w-7" />
        </div>
        <h2 className="mt-4 text-2xl font-bold text-gray-900 dark:text-zinc-100">
          {hcVerbiage.selectPatient[language]}
        </h2>
        <p className="mt-2 text-sm text-gray-400 dark:text-zinc-500">
          {hcVerbiage.selectPatientDescription[language]}
        </p>
      </div>

      <RadioGroup onChange={onSelect}>
        <div className="grid grid-cols-1 gap-y-3">
          {patients.map(patient => (
            <RadioGroup.Option
              key={patient.ID}
              value={patient}
              className={({ checked, active }) =>
                cn(
                  'portal-card relative flex cursor-pointer p-4 focus:outline-none transition-shadow',
                  checked ? 'ring-2 ring-[var(--primary-color)]' : '',
                  active ? 'ring-2 ring-[var(--primary-color)]' : ''
                )
              }
            >
              {({ checked }) => (
                <>
                  <span className="flex flex-1 items-center gap-4">
                    <span
                      className="inline-flex h-11 w-11 items-center justify-center rounded-xl text-white font-bold text-sm"
                      style={{ backgroundColor: 'var(--primary-color)' }}
                    >
                      {patient.fname?.charAt(0)?.toUpperCase()}
                      {patient.lname?.charAt(0)?.toUpperCase()}
                    </span>
                    <div className="flex flex-col">
                      <RadioGroup.Label
                        as="span"
                        className="text-sm font-semibold text-gray-900 dark:text-zinc-100"
                      >
                        {patient.fname} {patient.lname}
                      </RadioGroup.Label>
                    </div>
                  </span>
                  <CheckCircleIcon
                    className={cn(checked ? 'h-5 w-5 text-[var(--primary-color)]' : 'hidden')}
                    aria-hidden="true"
                  />
                </>
              )}
            </RadioGroup.Option>
          ))}
        </div>
      </RadioGroup>
    </div>
  );
};

export default PatientSelect;
